import { useQuery } from '@apollo/client';
import { GetFilters } from 'apollo/filters/filters.gql';
import { useCallback } from 'react';
import { FiltersQuery, FilterCategories } from 'types/generated/graphql';
import { i18n } from 'utils';
import { ApiFiltersContextType } from '../types';
import { createFilterFamilyMap } from '../helpers/createFilterFamilyMap';

export const defaultApiFilters: FilterCategories = {
    genres: [],
    instruments: [],
    sampleTypes: [],
    sfxs: [],
    tags: [],
    tagsCategories: [],
};

const transformFiltersData = (
    data: FiltersQuery['filters'],
): FilterCategories => {
    if (!data) return defaultApiFilters;

    return {
        ...data,
        tags:
            data.tags?.map((tag) => ({
                category: tag.category,
                code: tag.code,
                label: tag.label,
            })) || [],
        tagsCategories:
            data.tagsCategories?.map((category) => ({
                category: category.category,
                list: category.list.map((tag) => ({
                    code: tag.code,
                    label: tag.label,
                })),
            })) || [],
    };
};

const useApiFilters = (): {
    apiFilters: FilterCategories;
    getFilterFamily: ApiFiltersContextType['getFilterFamily'];
    loading: boolean;
} => {
    const { data, loading } = useQuery<FiltersQuery>(GetFilters, {
        notifyOnNetworkStatusChange: true,
    });

    // we can add other filters categories here if needed
    const filterFamilyMap = createFilterFamilyMap(data?.filters?.instruments);

    const getFilterFamily: ApiFiltersContextType['getFilterFamily'] =
        useCallback(
            (filter: string) => {
                const filterFamily = filterFamilyMap[filter];

                const filterFamilyLabel = filterFamily && i18n._(filterFamily);

                return filterFamilyLabel ?? undefined;
            },
            [filterFamilyMap],
        );

    return {
        apiFilters: transformFiltersData(data?.filters),
        getFilterFamily,
        loading,
    };
};

export default useApiFilters;
